import { useCallback, useEffect, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

import {
  autoLayoutSetter,
  createNotificationSetter,
  cursorAtom,
  nodesAtom,
  sideBarSelectedAtom,
  saveCanvasSetter,
  selectedNodeTypeAtom,
  useCopilotDrawerState,
  sessionAtom,
} from "@zapier/canvas-state";
import { AutoLayoutSuccessMessage } from "../AutoLayoutSuccessMessage";

export default function KeydownListeners() {
  const setAutoLayout = useSetAtom(autoLayoutSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const createNotification = useSetAtom(createNotificationSetter);
  const currentNodes = useAtomValue(nodesAtom);
  const [cursorMode, setCursorMode] = useAtom(cursorAtom);
  const [selectedNodeType, setSelectedNodeType] = useAtom(selectedNodeTypeAtom);
  const [, setIsCopilotOpen] = useCopilotDrawerState();
  const session = useAtomValue(sessionAtom);

  const [sideBarSelected, setToolbarSelected] = useAtom(sideBarSelectedAtom);

  const wasRepeating = useRef(false);

  const runAutoLayoutAndSave = useCallback(async () => {
    if (currentNodes.length === 0) return;

    const { nodes, isSuccess } = setAutoLayout();

    if (isSuccess) {
      saveToBackend({ nodes });

      createNotification({
        type: "success",
        message: <AutoLayoutSuccessMessage />,
      });
    }
  }, [setAutoLayout, createNotification, saveToBackend, currentNodes.length]);

  const toggleDevTools = useCallback(() => {
    if (sideBarSelected === "devTools") {
      setToolbarSelected(null);
    } else {
      // Always close copilot when we click to open other sidebar items or else the sidebar won't show up
      setIsCopilotOpen(false);
      setToolbarSelected("devTools");
    }
  }, [sideBarSelected, setToolbarSelected, setIsCopilotOpen]);

  const isInputField = (target: EventTarget | null) => {
    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target as HTMLElement)?.isContentEditable
    );
  };

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Cmd/Ctrl + Shift + Y for applying auto layout
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.code === "KeyY"
      ) {
        event.preventDefault(); // Prevent any default browser behavior
        runAutoLayoutAndSave();
      }

      // Cmd/Ctrl + D for toggling DevTools
      if ((event.metaKey || event.ctrlKey) && event.code === "KeyD") {
        if (session?.is_staff || session?.is_masquerade) {
          event.preventDefault(); // Prevent any default browser behavior
          toggleDevTools();
        }
      }

      if (event.code === "Space" && !isInputField(event.target)) {
        wasRepeating.current = event.repeat;
        // Only trigger when key is being held
        if (event.repeat) {
          if (selectedNodeType) {
            setSelectedNodeType(null);
          }

          setCursorMode("pan");
        }
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space" && !isInputField(event.target)) {
        // Only reset to select if we were in pan mode from holding space
        if (wasRepeating.current && cursorMode === "pan") {
          setCursorMode("select");
        }
        wasRepeating.current = false;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    // Clean up the event listener
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toggleDevTools,
    runAutoLayoutAndSave,
    cursorMode,
    setCursorMode,
    selectedNodeType,
    setSelectedNodeType,
  ]);

  return null;
}
